import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { MessageOutlined, RightOutlined } from "@ant-design/icons";
import { GlassCard, ActionButton } from "../../../shared/components";
import { usePageStyles } from "../../../shared/styles";
import { useColorMode } from "../../../contexts/color-mode";
import { useNavigate } from "react-router-dom";
import type { DashboardMessage } from "../types";

export interface MessagesCardProps {
    messages: DashboardMessage[];
    listUrl: string;
    accountId: number;
}

export function MessagesCard({ messages, listUrl, accountId }: MessagesCardProps) {
    const _t = useTranslate();
    const navigate = useNavigate();
    const styles = usePageStyles();
    const { mode } = useColorMode();
    const isDark = mode === "dark";

    if (!messages || messages.length === 0) {
        return (
            <GlassCard variant="emerald">
                <div className="flex items-center gap-3 mb-6">
                    <div style={styles.cardIcon("emerald")}>
                        <MessageOutlined className="text-xl" />
                    </div>
                    <h3 className="m-0 text-base font-semibold" style={{ color: styles.container.color }}>
                        {_t("messages.titles.latest", "Messages")}
                    </h3>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center text-center p-5">
                    <div
                        className="w-18 h-18 rounded-[20px] flex items-center justify-center mb-5 border"
                        style={{
                            background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                            borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                        }}
                    >
                        <MessageOutlined
                            className="text-4xl"
                            style={{
                                color: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                            }}
                        />
                    </div>
                    <p className="m-0 mb-2 text-[15px] font-semibold" style={{ color: styles.container.color }}>
                        {_t("dashboard.messages.noNew", "Pas de nouveaux messages")}
                    </p>
                    <p
                        className="m-0 mb-5 text-[13px] leading-relaxed"
                        style={{
                            color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                        }}
                    >
                        {_t("dashboard.messages.helpText", "Besoin d'aide avec une demande ?\nNotre équipe est disponible 24/7.")}
                    </p>
                    <ActionButton variant="primary" onClick={() => navigate(listUrl)} icon={<MessageOutlined />}>
                        {_t("dashboard.messages.contactSupport", "Contacter le support")}
                    </ActionButton>
                </div>
            </GlassCard>
        );
    }

    return (
        <GlassCard variant="emerald">
            <div className="flex items-center gap-3 mb-6">
                <div style={styles.cardIcon("emerald")}>
                    <MessageOutlined className="text-xl" />
                </div>
                <h3 className="m-0 text-base font-semibold" style={{ color: styles.container.color }}>
                    {_t("messages.titles.latest", "Messages")}
                </h3>
            </div>
            <div className="flex flex-col gap-3">
                {messages.slice(0, 3).map((message) => (
                    <div
                        key={message.id}
                        className="rounded-2xl px-5 py-4.5 cursor-pointer transition-all duration-200 border"
                        style={{
                            background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                            borderColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                                isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                                isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
                        }}
                        onClick={() => navigate(`/accounts/${accountId}/orders/show/${message.orderId}`)}
                    >
                        <div className="text-sm mb-2 line-clamp-2" style={{ color: styles.container.color }}>
                            {message.content}
                        </div>
                        <div className="flex justify-between items-center">
                            <span
                                className="text-xs"
                                style={{
                                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                                }}
                            >
                                <DateField value={message.createdAt} format="DD MMM HH:mm" />
                            </span>
                            <RightOutlined
                                className="text-xs"
                                style={{
                                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </GlassCard>
    );
}
